import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuArrowLeft, LuEye, LuSend, LuArchive, LuInfo, LuMonitorPlay, LuListChecks, LuBuilding, LuBarChart3, LuCheck, LuImage, LuTrash2,
} from 'react-icons/lu';
import TrainingContentTab from './TrainingContentTab';
import TrainingAssessmentsTab from './TrainingAssessmentsTab';
import TrainingReleaseTab from './TrainingReleaseTab';
import TrainingReportTab from './TrainingReportTab';
import TrainingPreviewModal from './TrainingPreviewModal';
import { COURSE_STATUS, Badge, errorMessage, resizeImageFile } from './trainingUtils';

const TABS = [
  { id: 'data', label: 'Dados', icon: <LuInfo /> },
  { id: 'content', label: 'Conteúdo', icon: <LuMonitorPlay /> },
  { id: 'assessments', label: 'Avaliações', icon: <LuListChecks /> },
  { id: 'release', label: 'Liberação', icon: <LuBuilding /> },
  { id: 'report', label: 'Relatórios', icon: <LuBarChart3 /> },
];

function DataTab({ course, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: course.title || '',
    description: course.description || '',
    workload_hours: course.workload_hours ?? '',
    passing_score: course.passing_score ?? 70,
    sequential: !!course.sequential,
    cover_image: course.cover_image || '',
    cover_image_portrait: course.cover_image_portrait || '',
    category: course.category || '',
  }));
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/training/categories').then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const handleImage = (field) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try { set(field, await resizeImageFile(file, 1200)); } catch (err) { setError(err.message); }
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/training/courses/${course.id}`, {
        ...form,
        workload_hours: form.workload_hours === '' ? null : Number(form.workload_hours),
        passing_score: Number(form.passing_score),
        cover_image: form.cover_image || null,
        cover_image_portrait: form.cover_image_portrait || null,
        category: form.category.trim() || null,
      });
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 820 }}>
      {error && <div className="alert-custom danger">{error}</div>}
      {saved && <div className="alert-custom success">Dados do curso salvos.</div>}
      <div>
        <label className="form-label">Título *</label>
        <input className="form-control" value={form.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div>
        <label className="form-label">Descrição</label>
        <textarea className="form-control" rows={4} value={form.description} onChange={(e) => set('description', e.target.value)}
          placeholder="Objetivo do curso, para quem é, o que o colaborador vai aprender" />
      </div>
      <div className="training-form-grid">
        <div>
          <label className="form-label">Coleção / trilha</label>
          <input className="form-control" list="training-categories" value={form.category} maxLength={100}
            placeholder="Ex.: Metodologia DHR" onChange={(e) => set('category', e.target.value)} />
          <datalist id="training-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          <div className="training-hint">Agrupa os cursos em faixas na tela do colaborador.</div>
        </div>
        <div>
          <label className="form-label">Carga horária (horas)</label>
          <input className="form-control" type="number" min="0" step="0.5" value={form.workload_hours} onChange={(e) => set('workload_hours', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Nota mínima de aprovação (0–100)</label>
          <input className="form-control" type="number" min="0" max="100" value={form.passing_score} onChange={(e) => set('passing_score', e.target.value)} />
          <div className="training-hint">Comparada com a média das melhores notas do aluno nas avaliações.</div>
        </div>
      </div>
      <label className="training-check">
        <input type="checkbox" checked={form.sequential} onChange={(e) => set('sequential', e.target.checked)} />
        Exigir conclusão sequencial (a próxima aula/avaliação só libera após concluir a anterior)
      </label>
      <div className="training-form-grid">
        {[
          { field: 'cover_image', label: 'Capa horizontal (16:9)', hint: 'Usada em "Continuar assistindo" e na gestão.', w: 220, h: 124 },
          { field: 'cover_image_portrait', label: 'Capa vertical (opcional, 9:16)', hint: 'Cards em pé das coleções. Sem ela, a capa horizontal é recortada.', w: 96, h: 170 },
        ].map((c) => (
          <div key={c.field}>
            <label className="form-label">{c.label}</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="training-course-cover" style={{ width: c.w, height: c.h, borderRadius: 8, ...(form[c.field] ? { backgroundImage: `url("${form[c.field]}")` } : {}) }}>
                {!form[c.field] && <LuImage />}
              </div>
              <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                <label className="training-btn training-btn--sm" style={{ marginBottom: 0 }}>
                  <LuImage /> Escolher imagem
                  <input type="file" accept="image/*" hidden onChange={handleImage(c.field)} />
                </label>
                {form[c.field] && (
                  <button type="button" className="training-btn training-btn--sm training-btn--danger" onClick={() => set(c.field, '')}><LuTrash2 /> Remover</button>
                )}
              </div>
            </div>
            <div className="training-hint">{c.hint}</div>
          </div>
        ))}
      </div>
      <div>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}>
          <LuCheck /> {saving ? 'Salvando…' : 'Salvar dados'}
        </button>
      </div>
    </div>
  );
}

export default function TrainingCourseEditor({ courseId, onBack }) {
  const [course, setCourse] = useState(null);
  const [tab, setTab] = useState('data');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [issues, setIssues] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/training/courses/${courseId}`);
      setCourse(data);
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar o curso'));
    }
  }, [courseId]);

  useEffect(() => { reload(); }, [reload]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  const handlePublish = async () => {
    setError(''); setIssues(null);
    try {
      await api.post(`/training/courses/${courseId}/publish`);
      flash('Curso publicado. Ele já aparece para as empresas liberadas.');
      reload();
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível publicar'));
      if (e.response?.data?.issues) setIssues(e.response.data.issues);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Arquivar este curso? Ele deixa de aparecer para novos alunos; quem já está matriculado mantém o histórico.')) return;
    try {
      await api.post(`/training/courses/${courseId}/archive`);
      flash('Curso arquivado.');
      reload();
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível arquivar'));
    }
  };

  if (!course) {
    return (
      <div className="page-card">
        <button type="button" className="training-btn" onClick={onBack}><LuArrowLeft /> Voltar</button>
        <div className="training-hint" style={{ marginTop: 16 }}>{error || 'Carregando…'}</div>
      </div>
    );
  }

  return (
    <div className="page-card">
      <div className="training-editor-header">
        <div>
          <button type="button" className="training-btn training-btn--sm" onClick={onBack} style={{ marginBottom: 10 }}>
            <LuArrowLeft /> Voltar aos cursos
          </button>
          <div className="training-editor-title">{course.title} <Badge meta={COURSE_STATUS[course.status]} /></div>
          <div className="page-card-subtitle">
            {course.enrollment_count} aluno{course.enrollment_count !== 1 ? 's' : ''} matriculado{course.enrollment_count !== 1 ? 's' : ''}
            {course.has_students && ' · conteúdo não pode mais ser excluído, apenas editado'}
          </div>
        </div>
        <div className="training-editor-actions">
          <button type="button" className="training-btn" onClick={() => setShowPreview(true)}><LuEye /> Pré-visualizar</button>
          {course.status !== 'published' && (
            <button type="button" className="training-btn training-btn--primary" onClick={handlePublish}><LuSend /> Publicar</button>
          )}
          {course.status === 'published' && (
            <button type="button" className="training-btn" onClick={handleArchive}><LuArchive /> Arquivar</button>
          )}
        </div>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {success && <div className="alert-custom success">{success}</div>}
      {(issues || (course.status !== 'published' && course.publish_issues.length > 0)) && (
        <div className="training-issues">
          <strong>{issues ? 'O curso não foi publicado. Falta:' : 'Pendências para publicar:'}</strong>
          <ul>{(issues || course.publish_issues).map((i) => <li key={i}>{i}</li>)}</ul>
        </div>
      )}
      {course.status === 'published' && course.publish_issues.length > 0 && (
        <div className="training-issues">
          <strong>Atenção: o curso publicado tem pendências</strong> (ex.: avaliação nova ainda sem questões). Os alunos não conseguem fazer essas avaliações até a correção.
          <ul>{course.publish_issues.map((i) => <li key={i}>{i}</li>)}</ul>
        </div>
      )}

      <div className="training-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
            className={`training-tab ${tab === t.id ? 'is-active' : ''}`} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'data' && <DataTab key={course.id} course={course} onSaved={reload} />}
      {tab === 'content' && <TrainingContentTab course={course} onChanged={reload} />}
      {tab === 'assessments' && <TrainingAssessmentsTab course={course} onChanged={reload} />}
      {tab === 'release' && <TrainingReleaseTab course={course} />}
      {tab === 'report' && <TrainingReportTab course={course} />}

      {showPreview && <TrainingPreviewModal courseId={course.id} onClose={() => setShowPreview(false)} />}
    </div>
  );
}
